import { object, string } from 'yup';
import { Checkbox, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import Button from '@mui/material/Button';
import './sendMailDialog.form.scss';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/use-auth.hook';
import { sendMail } from '../../../services/contact.service';
import { AxiosError } from 'axios';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { MailDto } from '../../../services/dtos/mail.dto';

/**
 * Dialog de formulaire
 *
 * @param {boolean} props.open - Etat du dialog
 * @param {function} props.handleClose - Fonction de fermeture du dialog
 * @param {Object} props.userInfos - Informations de l'utilsateur
 */
export default function SendMailDialogForm(props: {
    open: boolean;
    emails: string[];
    mandateId?: string;
    handleClose: () => void;
}) {
    // Booleen pour savoir si on envoie une copie du mail à l'utilisateur connecté
    const [copy, setCopy] = useState<boolean>(false);
    // Récupération du fichier jont dans l'envoi mail
    const [file, setFile] = useState<File | undefined>();
    // Booleen pour savoir si le formulaire est validé ou non
    const [validate, setValidate] = useState<boolean>(false);
    //Récupération de l'utilisateur connecté
    const auth = useAuth();
    //SnackBar pour les messages
    const showMessage = useSnackbars();

    useEffect(() => {
        if (!props.open) {
            setCopy(false);
            setFile(undefined);
            setValidate(false);
        }
    }, [props.open]);

    const handleCloseDialog = () => {
        setCopy(false);
        setFile(undefined);
        setValidate(false);
        props.handleClose();
    };

    // AJout d'une copie dans l'envoi de mail
    const handleChangeCopy = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCopy(event.target.checked);
    };

    //handler sur le changement de valeur du input file dans l'envoi de mail
    const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if ('files' in event.target) {
            setFile(event.target.files?.[0]);
        }
    };

    /**
     * Fonction d'envoi du formulaire
     *
     * @param {UserAccountDto} e - Infos renseignées
     */
    const handleSubmitSendMail = (e: MailDto) => {
        if (auth?.user?.id) {
            setValidate(true);
            const payload = {
                ...e,
                recipients: props.emails,
                copy,
            };
            // Envoi du mail
            sendMail(file ?? '', payload, props.mandateId)
                .then(data => {
                    showMessage.successAlert(data.status);
                    handleCloseDialog();
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setValidate(false);
                });
        }
    };

    /**
     * Represents a validation schema for a user's information.
     * @typedef {Object} ValidationSchema
     */
    const validationSchema = object({
        title: string().trim().required("L'objet ne peut pas être vide"),
        content: string().trim().required('Le message ne peut pas être vide'),
    });

    /**
     * Represents the initial values for a form.
     *
     */
    const iniValues: MailDto = {
        recipients: props.emails,
        title: '',
        content: '',
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleCloseDialog}
            className={'dialog-send-mail'}
            PaperProps={{ className: 'dialog-send-mail__paper' }}
            maxWidth="sm"
            fullWidth
        >
            <Formik initialValues={iniValues} onSubmit={handleSubmitSendMail} validationSchema={validationSchema}>
                <Form>
                    <DialogContent className="dialog-send-mail__content">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <InputForm id={'title'} name={'title'} label={'Objet'} placeholder="Objet du message" />
                            </Grid>
                            <Grid item xs={12}>
                                <InputForm
                                    id={'content'}
                                    name={'content'}
                                    label={'Message'}
                                    multiline
                                    rows={8}
                                    placeholder="Écrivez votre message"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputForm
                                    type="file"
                                    id="document"
                                    name="document"
                                    label={'Pièce jointe (facultatif)'}
                                    handleChange={handleChangeFile}
                                />
                                {file && (
                                    <Typography variant="body2" className="dialog-send-mail__attachmentName">
                                        Fichier : {file.name}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <div className="dialog-send-mail__copyRow">
                                    <div>
                                        <Typography variant="body1">Recevoir une copie</Typography>
                                        <Typography variant="body2">
                                            Ajoute votre adresse à la liste des destinataires.
                                        </Typography>
                                        {copy && (
                                            <Typography variant="body2" className="dialog-send-mail__copyHint">
                                                Une copie sera envoyee a votre adresse connectee.
                                            </Typography>
                                        )}
                                    </div>
                                    <Checkbox
                                        data-testid={'copy-checkbox'}
                                        onChange={handleChangeCopy}
                                        checked={copy}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className="dialog-send-mail__actions">
                        <Button variant="outlined" onClick={handleCloseDialog} data-testid={'close'}>
                            Retour
                        </Button>
                        <Button type="submit" data-testid={'submit'} variant="contained" disabled={validate}>
                            {validate ? 'Envoi...' : 'Envoyer'}
                        </Button>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}
